import type {
  DayTimeParting,
  DayTimePartingRange,
  ScheduleState,
  Weekday,
} from '@/entities/campaign/types';
import { FULL_DAY, getSelectedDays, WEEKDAYS } from '../constants';

const createTimeRangeId = () => Math.random().toString(36).substring(2, 11);

const buildFormTimeRanges = (ranges: DayTimePartingRange[]) =>
  ranges.map(range => ({
    id: createTimeRangeId(),
    start: range.start,
    end: range.end,
  }));

const getTimeRangesKey = (ranges: DayTimePartingRange[]) =>
  ranges.map(range => `${range.start}-${range.end}`).join('|');

export const normalizeDayTimeParting = (
  dayTimeParting?: DayTimeParting
): string =>
  JSON.stringify(
    Object.entries(dayTimeParting ?? {})
      .sort(([leftDay], [rightDay]) => leftDay.localeCompare(rightDay))
      .map(([day, ranges]) => [
        day,
        ranges
          .map(range => ({ start: range.start, end: range.end }))
          .sort((left, right) =>
            `${left.start}-${left.end}`.localeCompare(
              `${right.start}-${right.end}`
            )
          ),
      ])
  );

export const buildSchedulingFromDayTimeParting = (
  dayTimeParting: DayTimeParting
): ScheduleState | undefined => {
  const days = Object.keys(dayTimeParting);
  if (!days.length) return undefined;
  const selectedDays = days.map(day => day.toLowerCase() as Weekday);

  const firstRangesKey = getTimeRangesKey(dayTimeParting[days[0]]);
  const hasSameRangesForEveryDay =
    firstRangesKey.length > 0 &&
    days.every(day => getTimeRangesKey(dayTimeParting[day]) === firstRangesKey);

  if (hasSameRangesForEveryDay) {
    return {
      enableDayParting: true,
      enableTimeParting: true,
      selectedDays,
      allDays: buildFormTimeRanges(dayTimeParting[days[0]]),
      perDaySchedules: {},
    };
  }

  const perDaySchedules: ScheduleState['perDaySchedules'] = {};
  days.forEach(day => {
    const dayKey = day.toLowerCase() as Weekday;
    perDaySchedules[dayKey] = {
      enabled: true,
      ranges: buildFormTimeRanges(dayTimeParting[day]),
    };
  });

  return {
    enableDayParting: true,
    enableTimeParting: true,
    selectedDays,
    allDays: [],
    perDaySchedules,
  };
};

export const buildWorkingHours = (
  screenOnTime?: any,
  screenOnDays?: string[] | string
) => {
  const result: Record<string, any[]> = {};

  if (!screenOnTime || !screenOnDays) {
    return result;
  }
  const days = Array.isArray(screenOnDays)
    ? screenOnDays
    : JSON.parse(screenOnDays || '[]');

  const start = `${screenOnTime.from.hours}:${screenOnTime.from.minutes}`;
  const end = `${screenOnTime.to.hours}:${screenOnTime.to.minutes}`;

  days.forEach((day: string) => {
    result[day] = [
      {
        start,
        end,
      },
    ];
  });

  return result;
};

export const buildSlotDayTimeParting = (scheduling?: any) => {
  if (!scheduling) {
    return {};
  }

  const result: Record<
    string,
    {
      start: string;
      end: string;
    }[]
  > = {};

  const selectedDays = getSelectedDays(scheduling);

  if (scheduling.enableDayParting && !scheduling.enableTimeParting) {
    selectedDays.forEach((day: string) => {
      result[day.toUpperCase()] = FULL_DAY;
    });

    return result;
  }

  if (scheduling.enableTimeParting) {
    selectedDays.forEach((day: string) => {
      const daySchedule = scheduling.perDaySchedules?.[day];

      const ranges = daySchedule?.enabled
        ? daySchedule.ranges
        : scheduling.allDays;

      if (!result[day.toUpperCase()]) {
        result[day.toUpperCase()] = [];
      }

      if (!ranges?.length) {
        result[day.toUpperCase()].push(...FULL_DAY);
        return;
      }

      ranges.forEach((range: any) => {
        const isAllDaysRange = !daySchedule?.enabled;

        if (
          isAllDaysRange &&
          range.start &&
          range.end &&
          range.start > range.end
        ) {
          // Overnight range split
          result[day.toUpperCase()].push({
            start: range.start,
            end: '23:59',
          });

          const currentDayIndex = WEEKDAYS.findIndex(
            d => d.id === day.toLowerCase()
          );
          if (currentDayIndex !== -1) {
            const nextDayIndex = (currentDayIndex + 1) % 7;
            const nextDay = WEEKDAYS[nextDayIndex].id.toUpperCase();

            if (!result[nextDay]) {
              result[nextDay] = [];
            }
            result[nextDay].push({
              start: '00:00',
              end: range.end,
            });
          }
        } else {
          // Normal range
          result[day.toUpperCase()].push({
            start: range.start,
            end: range.end,
          });
        }
      });
    });

    return result;
  }

  return {};
};
